import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import * as teamService from '../services/teamService';
import * as personaService from '../services/personaService';
import { PersonaAvatar } from '../components/personas/PersonaAvatar';
import { TeamCapabilityChart } from '../components/teams/TeamCapabilityChart';
import { SkillCoverageChart } from '../components/teams/SkillCoverageChart';
import { SkillHeatmap } from '../components/teams/SkillHeatmap';

export const TeamDetail: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  
  const [teamPersonas, setTeamPersonas] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'skills' | 'heatmap'>('overview');
  
  const { 
    data: team, 
    isLoading: isLoadingTeam, 
    error: teamError 
  } = useQuery(
    ['team', teamId],
    () => teamService.getTeam(teamId)
  );
  
  const {
    data: analysis,
    isLoading: isLoadingAnalysis,
    error: analysisError
  } = useQuery(
    ['teamAnalysis', teamId],
    () => teamService.analyzeTeam(teamId),
    {
      enabled: !!team
    }
  );
  
  // Fetch personas for the team's roster
  useQuery(
    ['rosterPersonas', team?.roster_id],
    async () => {
      if (!team?.roster_id) return [];
      
      try {
        // In a real implementation, you would fetch personas from the roster
        const allPersonas = await personaService.getPersonas();
        setTeamPersonas(allPersonas);
        return allPersonas;
      } catch (error) {
        console.error('Failed to load roster personas:', error);
        return [];
      }
    },
    {
      enabled: !!team?.roster_id
    }
  );
  
  const deleteTeamMutation = useMutation(
    () => teamService.deleteTeam(teamId),
    {
      onSuccess: () => {
        navigate('/teams');
      }
    }
  );
  
  const handleDeleteTeam = () => {
    if (confirm('Are you sure you want to delete this team?')) {
      deleteTeamMutation.mutate();
    }
  };
  
  if (isLoadingTeam) {
    return <div className="text-center py-10">Loading team details...</div>;
  }
  
  if (teamError) {
    return <div className="text-center py-10 text-red-600">Error loading team details</div>;
  }
  
  if (!team) {
    return <div className="text-center py-10 text-red-600">Team not found</div>;
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">{team.name}</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate(`/teams/${teamId}/edit`)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Edit Team
          </button>
          <button
            onClick={handleDeleteTeam}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Delete Team
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Team Details</h2>
              
              <div className="flex border rounded-md overflow-hidden">
                <button
                  className={`px-3 py-1 text-sm ${
                    activeTab === 'overview' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </button>
                <button
                  className={`px-3 py-1 text-sm ${
                    activeTab === 'skills' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveTab('skills')}
                >
                  Skill Coverage
                </button>
                <button
                  className={`px-3 py-1 text-sm ${
                    activeTab === 'heatmap' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setActiveTab('heatmap')}
                >
                  Skill Heatmap
                </button>
              </div>
            </div>
            
            {activeTab === 'overview' && (
              <>
                <div className="mb-4">
                  <h3 className="text-lg font-medium mb-2">Description</h3>
                  <p className="text-gray-700">{team.description || 'No description provided.'}</p>
                </div>
                
                <div className="mb-4">
                  <h3 className="text-lg font-medium mb-2">Goal</h3>
                  <p className="text-gray-700">{team.goal}</p>
                </div>
                
                <div className="mb-4">
                  <h3 className="text-lg font-medium mb-2">Roster</h3>
                  <p className="text-gray-700">{team.roster_id}</p>
                </div>
                
                {team.leader_persona && (
                  <div className="mb-4">
                    <h3 className="text-lg font-medium mb-2">Leader</h3>
                    <LeaderPersonaCard personaId={team.leader_persona} />
                  </div>
                )}
                
                <div>
                  <h3 className="text-lg font-medium mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {team.tags.map(tag => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                    {team.tags.length === 0 && (
                      <p className="text-gray-500">No tags</p>
                    )}
                  </div>
                </div>
              </>
            )}
            
            {activeTab === 'skills' && (
              <SkillCoverageChart personas={teamPersonas} />
            )}
            
            {activeTab === 'heatmap' && (
              <SkillHeatmap personas={teamPersonas} />
            )}
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Workflow</h2>
            
            <div className="space-y-4">
              {team.workflow && team.workflow.length > 0 ? (
                <ol className="list-decimal list-inside space-y-2">
                  {team.workflow.map((step, index) => (
                    <li key={index} className="text-gray-700">{step}</li>
                  ))}
                </ol>
              ) : (
                <p className="text-gray-500">No workflow defined</p>
              )}
            </div>
          </div>
        </div>
        
        <div>
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Team Analysis</h2>
            
            {isLoadingAnalysis ? (
              <div className="text-center py-4">Loading analysis...</div>
            ) : analysisError ? (
              <div className="text-center py-4 text-red-600">Error loading analysis</div>
            ) : analysis ? (
              <div>
                {/* Radar chart visualization */}
                <div className="mb-6">
                  <TeamCapabilityChart 
                    teamAnalysis={analysis}
                    personas={teamPersonas}
                  />
                </div>
                
                {/* Original capability bars */}
                <div className="mb-4">
                  <h3 className="text-lg font-medium mb-2">Capabilities</h3>
                  <div className="space-y-2">
                    {Object.entries(analysis.capabilities).map(([capability, value]) => (
                      <div key={capability}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-600 capitalize">{capability}</span>
                          <span className="text-sm font-medium">{value}/10</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${(value / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-2">Role Coverage</h3>
                  <div className="space-y-2">
                    {Object.entries(analysis.roles_coverage).map(([role, count]) => (
                      <div key={role} className="flex justify-between items-center">
                        <span className="text-gray-700">{role}</span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {count} {count === 1 ? 'member' : 'members'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No analysis available</p>
            )}
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Metadata</h2>
            
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Created:</span>
                <span className="ml-2 text-gray-600">
                  {new Date(team.created_at).toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">Last Updated:</span>
                <span className="ml-2 text-gray-600">
                  {new Date(team.updated_at).toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">ID:</span>
                <span className="ml-2 text-gray-600">{team.id}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Leader persona card component
const LeaderPersonaCard = ({ personaId }) => {
  const { data: persona, isLoading, error } = useQuery(
    ['persona', personaId],
    () => personaService.getPersona(personaId)
  );
  
  if (isLoading) {
    return <div className="text-center py-2">Loading persona...</div>;
  }
  
  if (error || !persona) {
    return (
      <div className="text-red-600">Error loading persona {personaId}</div>
    );
  }
  
  return (
    <div className="flex items-center">
      <PersonaAvatar
        name={persona.name}
        specialization={persona.primarySpecialization}
        avatarUrl={persona.avatarUrl}
        size="medium"
      />
      <div className="ml-3">
        <div className="font-medium">{persona.name}</div>
        <div className="text-sm text-gray-500">{persona.primarySpecialization}</div>
      </div>
    </div>
  );
};
